import apiClient from "@toothfairy/shared-api/apiClient";
import secureRequest from "@toothfairy/shared-api/secureAPI";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import envConfig from "../../../envConfig";

const getCosts = async ({ workspaceId, month, year, billingPeriodStart, billingPeriodEnd, token, includePerDayAndType, excludePerMethodperDay, includeChildren, page, pageSize }) => {
  let url;
  if (billingPeriodStart && billingPeriodEnd) {
    url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/monthCosts?workspaceId=${workspaceId}&billingPeriodStart=${encodeURIComponent(billingPeriodStart)}&billingPeriodEnd=${encodeURIComponent(billingPeriodEnd)}`;
  } else {
    url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/monthCosts?workspaceId=${workspaceId}&month=${month}&year=${year}`;
  }
  if (includePerDayAndType !== undefined) url += `&includePerDayAndType=${includePerDayAndType}`;
  if (excludePerMethodperDay !== undefined) url += `&excludePerMethodperDay=${excludePerMethodperDay}`;
  if (includeChildren !== undefined) url += `&includeChildren=${includeChildren}`;
  if (page !== undefined) url += `&page=${page}`;
  if (pageSize !== undefined) url += `&pageSize=${pageSize}`;
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    false,
    headers,
  );
};

const getStripeCustomer = async () => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/customer`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const getStripeCheckout = async () => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/checkout`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const saveStripeCustomer = async (requestBody) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/customer`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    url,
    null,
    requestBody,
    true,
    null,
    payload,
  );
};

const getStripePortal = async () => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/portal`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const getStripeClientSecret = async () => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/clientSecret`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const getStripeClientInvoices = async (starting_after) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/invoices`;
  if (starting_after) url = url + `?starting_after=${starting_after}`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

// Unified billing history (recharges + invoice payments). Recharges are
// Checkout mode=payment purchases → Stripe charges with invoice=null, so they
// never appear in /billing/invoices. This endpoint reads charges (with the
// invoice expanded) and returns a normalized, deduped list keyed by charge id.
const getStripeClientBillingHistory = async (starting_after) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/history`;
  if (starting_after) url = url + `?starting_after=${starting_after}`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const getClientPaymentMethods = async () => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/clientPaymentMethods`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const collectPaymentsFromSubscription = async (requestBody) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/collectPaymentsFromSubscription`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    url,
    null,
    requestBody,
    true,
    null,
    payload,
  );
};

const resumeConsumptionWorkspace = async (requestBody) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/resumeWorkspace`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    url,
    null,
    requestBody,
    true,
    null,
    payload,
  );
};

const deleteClientPaymentMethod = async (requestBody) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/deletePaymentMethod`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    url,
    null,
    requestBody,
    true,
    null,
    payload,
  );
};

const createClientPaymentIntent = async (requestBody) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/createPaymentIntent`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    url,
    null,
    requestBody,
    true,
    null,
    payload,
  );
};

const updateUsageForWorkspace = async (requestBody) => {
  let url = `${envConfig.REACT_APP_REST_ENDPOINT}/billing/updateUsageRecordsForWorkspace`;
  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "POST",
    url,
    null,
    requestBody,
    true,
    null,
    payload,
  );
};

export default {
  getCosts,
  getStripeCustomer,
  getStripeCheckout,
  saveStripeCustomer,
  getStripePortal,
  getStripeClientSecret,
  getStripeClientInvoices,
  getStripeClientBillingHistory,
  getClientPaymentMethods,
  deleteClientPaymentMethod,
  createClientPaymentIntent,
  updateUsageForWorkspace,
  collectPaymentsFromSubscription,
  resumeConsumptionWorkspace,
};
